<template>
  <v-btn
    v-tooltip="'Importar cadeiras'"
    icon="fa-regular fa-file-import"
    color="primary"
    :loading="loader.loading"
    @click="open"
  />
  <v-dialog v-model="dialog" persistent max-width="640px">
    <v-form ref="form" @submit.prevent>
      <v-card>
        <v-toolbar color="primary" dark class="pl-6">
          <div class="d-flex flex-column">
            <span class="text-h6">Importar cadeiras</span>
            <span class="text-caption">{{ mapping.name }}</span>
          </div>
        </v-toolbar>
        <v-card-text class="d-flex flex-column ga-4">
          <div class="text-body-2">
            Envie um arquivo CSV (separado por vírgula ou ponto e vírgula, até 2 MB) com as colunas
            <strong>sector_id</strong>, <strong>section</strong>, <strong>row</strong>, <strong>row_order</strong>,
            <strong>name</strong>, <strong>position</strong> e <strong>type</strong> (standard, wheelchair, plus_size ou
            reduced_mobility), e opcionalmente <strong>label</strong>.
          </div>
          <div class="text-body-2">
            Cada seção do arquivo é substituída por completo: filas e cadeiras dela que não estiverem no arquivo são
            removidas. Se alguma linha tiver erro, nada é gravado. Use <strong>Validar</strong> para conferir o
            relatório antes de importar.
          </div>
          <v-file-input
            v-model="file"
            accept=".csv,text/csv"
            :show-size="false"
            label="Arquivo CSV"
            variant="outlined"
            density="compact"
            hide-details="auto"
            prepend-icon=""
            prepend-inner-icon="fa-regular fa-file-csv"
            :disabled="loader.loading"
            :rules="[(f: File | null) => !!f || 'Selecione um arquivo para importar.']"
          />
        </v-card-text>
        <v-card-actions>
          <v-btn
            variant="text"
            prepend-icon="fa-regular fa-download"
            :disabled="loader.loading"
            @click="downloadTemplate"
          >
            Baixar modelo
          </v-btn>
          <v-spacer />
          <v-btn
            color="red darken-1"
            variant="text"
            :disabled="loader.loading"
            @click="dialog = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            variant="text"
            :loading="loader.loading"
            @click="submit(true)"
          >
            Validar
          </v-btn>
          <v-btn
            color="green darken-1"
            variant="text"
            :loading="loader.loading"
            @click="submit(false)"
          >
            Importar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script lang="ts" setup>
import { ref, useTemplateRef } from 'vue'
import { isAxiosError } from 'axios'
import type { VForm } from 'vuetify/lib/components'
import MappingService from '@/services/MappingService'
import { describeSeatImportSummary, readSeatImportSummary, seatImportTemplate } from '@/helpers/seatImport'
import { useLoader } from '@/stores/loader'
import { useSnackBar } from '@/stores/snackbar'
import Mapping from '@/types/Mapping'

const snackbar = useSnackBar()
const loader = useLoader()

const { mapping } = defineProps<{ mapping: Mapping }>()
const emits = defineEmits<(e: 'import') => void>()

const form = useTemplateRef<VForm>('form')

const dialog = ref(false)
const file = ref<File | null>(null)

const service = new MappingService()

function open() {
  file.value = null
  dialog.value = true
}

function download(content: string, name: string) {
  const url = URL.createObjectURL(new Blob(['\ufeff', content], { type: 'text/csv' }))
  const link = document.createElement('a')

  link.href = url
  link.download = name
  link.click()

  URL.revokeObjectURL(url)
}

function downloadTemplate() {
  download(seatImportTemplate(), 'modelo_importacao_cadeiras.csv')
}

function downloadReport(content: string) {
  download(content, `relatorio_importacao_cadeiras_${(file.value?.name ?? 'cadeiras').replace(/\.[^.]+$/, '')}.csv`)
}

async function submit(dryRun: boolean) {
  await form.value?.validate()

  if (!form.value?.isValid || !file.value) return

  try {
    loader.increment()

    const response = await service.importSeats(mapping.id, file.value, dryRun)
    const summary = describeSeatImportSummary(readSeatImportSummary(response.headers))

    downloadReport(response.data)

    if (dryRun) {
      snackbar.show(`Validação concluída, nada foi gravado: ${summary} O relatório foi baixado.`, 'success', 8000)

      return
    }

    snackbar.show(`Importação concluída: ${summary} O relatório foi baixado.`, 'success', 8000)

    dialog.value = false
    emits('import')
  } catch (error) {
    // A file with errors comes back as a 422 holding the same CSV report
    if (isAxiosError(error) && error.response?.status === 422 && typeof error.response.data === 'string') {
      const summary = describeSeatImportSummary(readSeatImportSummary(error.response.headers))

      downloadReport(error.response.data)

      snackbar.show(`O arquivo tem erros e nada foi gravado: ${summary} O relatório foi baixado.`, 'error', 8000)

      return
    }

    console.error('Error importing seats:', error)
  } finally {
    loader.decrement()
  }
}
</script>
